import { sidebarConfig, type SidebarItemConfig, type UserRole } from "@/pages/sidebar.config";

/**
 * Titles for routes the sidebar does not list (or lists only for the other
 * workspace), matched on the path or any sub-path of it.
 */
const FALLBACK_TITLES: Record<string, string> = {
  "/home": "Home",
  "/explore": "Explore",
  "/dashboard": "Dashboard",
  "/goals": "Goals",
  "/profile": "Profile",
  "/programs": "Programs",
  "/peer-advisory/applications": "Applications",
  "/peer-advisory": "Programs",
  "/organisation-programs": "Programs",
  "/journey-portfolio": "My Journey",
  "/pab": "Personal Advisory Board",
  "/sessions": "Sessions",
  "/stars": "S.T.A.R.s",
  "/mindshare": "Feed",
  "/feed": "Feed",
  "/directory": "Directory",
  "/events": "Events",
  "/admin": "Admin",
  "/getting-started": "Getting Started",
};

const matches = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

/** The longest matching path wins, so /peer-advisory/applications beats /peer-advisory. */
const longestMatch = <T>(pathname: string, entries: [string, T][]): T | undefined =>
  entries
    .filter(([path]) => matches(pathname, path))
    .sort(([a], [b]) => b.length - a.length)[0]?.[1];

/**
 * The header title for a route: the label of the sidebar item it belongs to
 * in the active workspace — the same item the sidebar highlights — else a
 * fallback for routes outside the nav.
 */
export const getPageTitle = (pathname: string, role: UserRole): string | undefined => {
  const { menu, groups = [] } = sidebarConfig[role];
  const items: SidebarItemConfig[] = [...menu, ...groups.flatMap((g) => g.items)];

  const navEntries: [string, string][] = items.flatMap((item) =>
    // Query deep links (organisation tabs) share one path; skip them.
    item.path && !item.path.includes("?") ? [[item.path, item.label] as [string, string]] : [],
  );
  const fromNav = longestMatch(pathname, navEntries);
  if (fromNav) return fromNav;

  return longestMatch(pathname, Object.entries(FALLBACK_TITLES));
};
